import { Download } from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import {
	SovosButton,
	SovosFileUpload,
	SovosRadioGroup,
	SovosTypography,
} from 'mosaic-react';
import React, { useState } from 'react';

import {
	downloadGoodServiceCodesTemplate,
	uploadGoodServiceCodesFile,
} from 'src/api/goodServiceCodes';
import FormStepLayout from 'src/components/FormStepLayout';
import type { SovosUploadFile } from 'src/interfaces/SovosUploadFile';
import type {
	NewRateFileFormState,
	NewRateFileFormStep,
} from 'src/pages/newRateFile/NewRateFileFormTypes';

const StepGoodServiceCodes: NewRateFileFormStep<{
	selectedCodes?: NewRateFileFormState['rateFileGscList'];
	setSelectedCodes: (codes: NewRateFileFormState['rateFileGscList']) => void;
	selectedFileName?: NewRateFileFormState['rateFileGscListFileName'];
	setSelectedFileName: (
		fileName: NewRateFileFormState['rateFileGscListFileName']
	) => void;
	selectedSettings?: NewRateFileFormState['settings'];
	setSelectedSettings?: (settings: NewRateFileFormState['settings']) => void;
	gscLimit: number | null;
}> = ({
	onContinue,
	selectedCodes,
	setSelectedCodes,
	selectedFileName,
	setSelectedFileName,
	selectedSettings,
	setSelectedSettings,
	gscLimit,
}) => {
	const downloadGoodServiceCodesTemplateMutation = useMutation(
		downloadGoodServiceCodesTemplate
	);

	const [gscFiles, setGscFiles] = useState<SovosUploadFile[]>(() => {
		if (selectedFileName) {
			const file = new File(
				[selectedFileName],
				selectedFileName
			) as SovosUploadFile;

			file.progress = { loading: false, percent: 100 };

			return [file];
		}

		return [];
	});

	const uploadFileMutation = useMutation(uploadGoodServiceCodesFile, {
		onSuccess: (data) => {
			setGscFiles((files) => {
				const currentFile = files?.[0];

				if (currentFile) {
					const updatedFile = new File(
						[currentFile],
						currentFile.name
					) as SovosUploadFile;

					updatedFile.progress = { loading: false, percent: 100 };

					setSelectedCodes(data);
					setSelectedFileName(currentFile.name);

					return [updatedFile];
				}

				return files;
			});
		},
		onError: () => {
			setGscFiles((files) => {
				const currentFile = files?.[0];

				if (currentFile) {
					const updatedFile = new File(
						[currentFile],
						currentFile.name
					) as SovosUploadFile;

					updatedFile.progress = { loading: false, percent: 0 };

					setSelectedCodes(undefined);
					setSelectedFileName(undefined);
					setShowErrors(true);

					return [updatedFile];
				}

				return files;
			});
		},
	});

	const [showErrors, setShowErrors] = useState(false);

	const validations = [
		{
			isValid:
				selectedSettings?.gtdGsc ||
				uploadFileMutation.isError ||
				Boolean(selectedCodes && selectedCodes.length > 0),
			message: 'Please upload your Good/Service codes',
		},
		{
			isValid: !uploadFileMutation.isError,
			message: 'Mapping File is not correct, review and try again',
		},
	];

	const errors = validations
		.filter(({ isValid }) => !isValid)
		.map(({ message }) => message);

	const handleContinue = () => {
		if (errors.length === 0) {
			onContinue();
		} else if (!showErrors) {
			setShowErrors(true);
		}
	};

	const resetFileValues = () => {
		uploadFileMutation.reset();
		setGscFiles([]);
		setSelectedCodes(undefined);
		setSelectedFileName(undefined);
		setShowErrors(false);
	};

	// @ts-ignore
	return (
		<FormStepLayout
			title='How would you like to add Good/Service codes?'
			onContinue={handleContinue}
			errors={errors}
			showErrors={showErrors}
			isLoading={uploadFileMutation.isLoading}
		>
			<SovosRadioGroup
				options={[
					{
						label: 'Import existing Good/Service codes',
						value: 'true',
					},
					{
						label: 'Upload file',
						value: 'false',
					},
				]}
				value={selectedSettings?.gtdGsc}
				onChange={(_: any, value: string) => {
					if (setSelectedSettings) {
						setSelectedSettings({
							...selectedSettings,
							gtdGsc: value === 'true' ? true : false,
						});
						resetFileValues();
					}
				}}
			/>
			{!selectedSettings?.gtdGsc && (
				<>
					<SovosTypography variant='h6' sx={{ mb: 1 }}>
						1. Download and update the Good/Service code template
					</SovosTypography>
					<SovosTypography>
						Need help creating a Good/Service code map? Reach out to your
						Professional Services representative or our Sovos Support team.
					</SovosTypography>
					<SovosButton
						variant='text'
						startIcon={<Download />}
						disabled={downloadGoodServiceCodesTemplateMutation.isLoading}
						sx={{ mt: 2, mb: 4 }}
						onClick={() => downloadGoodServiceCodesTemplateMutation.mutate()}
					>
						Download template
					</SovosButton>

					<SovosTypography variant='h6' sx={{ mb: 1 }}>
						2. Upload your file
					</SovosTypography>
					<SovosTypography>
						Drag and drop the updated import template in a CSV format.
					</SovosTypography>
					{gscLimit && (
						<SovosTypography>
							The Good/Service code limit per file is {gscLimit}.
						</SovosTypography>
					)}

					<SovosFileUpload
						multiple={false}
						acceptedTypes='csv'
						fileList={gscFiles}
						onFilesSelected={(selectedFiles: SovosUploadFile[]) => {
							if (selectedFiles.length > 0) {
								setShowErrors(false);
								uploadFileMutation.mutate(selectedFiles[0]);
								setGscFiles(
									selectedFiles.map((file) => {
										file.progress = { loading: true, percent: null };

										return file;
									})
								);
							}
						}}
						onFileDelete={() => {
							resetFileValues();
						}}
						sx={{
							my: 3,
							borderColor:
								showErrors && errors.length > 0 ? 'error.main' : undefined,
						}}
					/>
				</>
			)}
		</FormStepLayout>
	);
};

export default StepGoodServiceCodes;
